import { authApi } from '@/api/authApi'
import { signingApi } from '@/api/signingApi'

/**
 * Exchange the signer's password for a single-use signing token (21 CFR Part 11).
 *
 * This replaces the typed-name rule `lib/signature.ts` describes: a name anyone
 * could type is an assertion, while a password checked by the server is an
 * authentication. The token that comes back is spent by the signing endpoint
 * itself, so the proof travels with the action rather than being a promise the
 * client makes about one it already did.
 *
 * The server answers a bad password with 400, never 401 — a 401 would trip the
 * SPA's global "session is dead" handler and sign the operator out over a typo.
 * Surface the failure with `apiErrorMessage`, like any other request.
 */
export async function mintSigningToken(password: string): Promise<string> {
  const { token } = await authApi.createSigningToken(password)
  passwordVerified?.(password)
  return token
}

type PasswordListener = (password: string) => void
let passwordVerified: PasswordListener | null = null

/**
 * Called with the signed-in user's own password each time the server accepts
 * it for a signature. The mobile app uses it to keep the offline signing key
 * set up and locked with the current password (z8nrz7fh5c); the desktop app
 * registers nothing. Never called for "Sign as" (someone else's password).
 */
export function onSigningPasswordVerified(listener: PasswordListener | null): void {
  passwordVerified = listener
}

/**
 * Mint a token for whoever is SIGNING: the signed-in user when `signerId` is
 * null or their own id, otherwise the person picked in "Sign as:" / "Remove
 * as:" (z8nrz7dt3z), who has just typed THEIR password at this workstation.
 *
 * Either way the token is spendable only in this session, and the server
 * records its signer — never a name the client supplies.
 */
export async function mintSigningTokenFor(
  password: string,
  signerId: string | null,
  selfId: string | null,
): Promise<string> {
  if (signerId === null || signerId === selfId) return mintSigningToken(password)
  const { token } = await signingApi.createSigningTokenAs(signerId, password)
  return token
}
